import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { RoomDetail } from "../../../api/room";
import styles from "./RoomLobby.module.scss";
import IconCopy from "../../../components/icons/IconCopy";

interface RoomLobbyProps {
  room: RoomDetail;
  isCreator: boolean;
  onStart: () => void;
  onClose: () => void;
  loading: boolean;
  error: string;
}

const RoomLobby: React.FC<RoomLobbyProps> = ({
  room,
  isCreator,
  onStart,
  onClose,
  loading,
  error,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(room.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/room/${room.code}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.lobby}>
      <div className={styles.lobbyHeading}>
        <div className={styles.title}>
          {t("room_waiting_participants", "Waiting for all participants")}
          <div className={styles.waitingDots}>
             <span />
              <span />
              <span />
          </div>
        </div>

        <span className={styles.codeLabel}>
          {t("room_code_label", "Room code")}
        </span>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.roomCode}>
        <div className={styles.roomCodeHolder}>
          <span className={styles.codeLabel}>{t("room_enter_code", "Enter room code")}</span>
          <span className={styles.codeValue} onClick={handleCopyCode}>
            {room.code}
          </span>
        </div>
        <button className={styles.copyLinkBtn} onClick={handleCopyLink}>
          <IconCopy/>
        </button>

        {copied ? (<span className={styles.copyLinkText}>{t("room_copied", "Copied!")}</span>) : ''}
      </div>

      <div className={styles.participantsListTitle}>{t("room_participants", "Participants")}</div>

      <div className={styles.participantsList}>
        {room.participants.map((p) => (
          <div key={p.user_id} className={styles.participant}>
            <div className={styles.participantBox}>
              <div className={styles.avatar}>
                {p.picture_url ? (
                  <img
                    src={p.picture_url}
                    alt={p.username}
                    onError={(e) => {
                      const target = e.currentTarget;
                      target.style.display = "none";
                      target.nextElementSibling?.removeAttribute("style");
                    }}
                  />
                ) : null}
                <span style={p.picture_url ? { display: "none" } : undefined}>
                  {(p.username || "U").charAt(0).toUpperCase()}
                </span>
              </div>
              <span className={styles.participantName}>{p.username}</span>
            </div>

            <div className={styles.participantBlock}>
              <span
                className={`${styles.dot} ${
                  !p.is_active ? styles.dotInactive : ""
                }`}
              />
              <span className={styles.creatorBadge}>
                {p.user_id === room.creator_id ? t("room_owner", "Owner") : t("room_member", "Member")}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.actions}>
        {isCreator && room.participants.length >= 2 && (
          <button
            className={styles.btnPrimary}
            onClick={onStart}
            disabled={loading}
          >
            {t("room_start", "Start Session")}
          </button>
        )}
        {isCreator && (
          <button className={styles.btnDanger} onClick={onClose}>
            {t("room_cancel", "Cancel Room")}
          </button>

        )}
      </div>
    </div>
  );
};

export default RoomLobby;
